import { ReactNode } from "react";
import { Lock, Loader2 } from "lucide-react";
import { useRole } from "@/hooks/useRole";

/**
 * Defense-in-depth guard for admin-only widgets that may be embedded
 * outside the AdminRoute-protected portal. Renders a clear empty state
 * for non-admins instead of silently hiding the surface (so QA can
 * verify the gate is working).
 */
export default function AdminOnly({
  children,
  feature,
}: {
  children: ReactNode;
  /** Short label for the locked-state message, e.g. "Sentry Health Check". */
  feature: string;
}) {
  const { isAdmin, loading } = useRole();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-5 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking permissions…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0">
            <Lock className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-base font-bold text-[#1F3864] mb-1">{feature} is admin-only</h3>
            <p className="text-sm text-gray-600">
              You need an admin role to view or run this tool. If you believe this is a mistake,
              ask a workspace owner to grant you the <code className="font-mono">admin</code> role
              from <span className="font-semibold">Admin Portal → Team</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}